import type { DOMConversionMap } from 'lexical';
import { $createTextNode } from 'lexical';

/**
 * How pasted `text/html` is converted on its way into the editor.
 *
 * Note there is no explicit style stripping here, and none is needed.
 * `@lexical/html` reads only the CSS properties that map to a
 * `TextFormatType` — `font-weight` to bold, `font-style` to italic,
 * `text-decoration` to underline/strikethrough. `color`, `background-color`,
 * `font-family` and `font-size` have no format to map to and are ignored
 * outright, so nothing is written to `TextNode.style`. That is emergent
 * behaviour rather than configuration, so `textarea-paste-conversion.test.ts`
 * pins it.
 *
 * Pasted `<img>` elements have no node to import into — markdown cannot store
 * an image — and an element that yields no node makes Lexical trim the
 * whitespace on either side of it, so `before <img> after` arrives as
 * `beforeafter`. Converting it to a single space keeps the neighbouring words
 * apart; the image itself is still dropped.
 */
const htmlImport: DOMConversionMap = {
  img: () => ({
    conversion: () => ({ node: $createTextNode(' ') }),
    priority: 0,
  }),
};

/**
 * Bullet characters people paste as plain text. `-` is deliberately absent: it
 * is already the target format, so matching it only ever rewrote the leading
 * whitespace that encodes nesting.
 */
const PLAIN_TEXT_BULLETS = ['•', '●', '○', '§', '·', 'o'];

/**
 * Rewrites bullet-looking plain text into markdown lists.
 *
 * Only `text/plain` needs this. The `text/html` a normal copy from Word or
 * Google Docs puts on the clipboard already imports as real list nodes; this
 * covers what does not — paste-as-plain-text, Notepad, terminal output,
 * plain-text mail.
 *
 * Leading spaces and tabs are preserved throughout, because `@lexical/markdown`
 * reads them as list nesting (`/^(\s*)[-*+]\s/`). Matching is confined to
 * spaces and tabs rather than `\s` so a rule cannot consume the newline that
 * ends the previous line.
 */
const normalizePlainTextBullets = (text: string) =>
  PLAIN_TEXT_BULLETS.reduce(
    // A trailing space is required after `o`, or it would eat the first letter
    // of any word beginning with one.
    (result, bullet) =>
      result.replaceAll(
        new RegExp(
          String.raw`(^|\n)([ \t]*)${bullet}[ \t]${bullet === 'o' ? '+' : '*'}`,
          'g'
        ),
        '$1$2- '
      ),
    text
    // `1)` and `1.` both become `1. `, since Lexical's ordered-list rule accepts
    // only the dot form (`/^(\s*)(\d{1,})\.\s/`).
  ).replaceAll(/(^|\n)([\t ]*)(\d+)[).][\t ]*/g, '$1$2$3. ');

/**
 * Whether a pasted html payload is nothing but an `<img>`.
 *
 * **This is what a browser hands over for an image copied from a page**: the page's own `<img>`,
 * and the image itself as a file beside it. There is nothing here to write - the file is the
 * paste - so a caller taking the file wants the html ignored rather than imported, which would
 * leave behind the single space `htmlImport` gives an image.
 *
 * `meta`, `style` and `link` are discounted because some browsers prefix the payload with them.
 * Any real content alongside the image - a word, a second element - means it is a rich paste that
 * happens to contain an image, and `htmlImport` is right to keep its place with a space.
 */
const isImageOnlyHtml = ({ html }: { html: string }) => {
  if (!html.trim()) return false;

  const { body } = new DOMParser().parseFromString(html, 'text/html');
  const elements = body.querySelectorAll('*:not(meta):not(style):not(link)');

  return (
    body.textContent.trim() === '' &&
    elements.length === 1 &&
    elements.item(0).tagName === 'IMG'
  );
};

export const TextareaPasteConversion = {
  htmlImport,
  isImageOnlyHtml,
  normalizePlainTextBullets,
};
